import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  Btn, Badge, StatusBadge, Card, CardHeader, Table, Tr, Td,
  FormGroup, Input, Select, Textarea, Modal, StatCard, Alert,
  Spinner, SectionDivider, fmtAmt, fmtDate
} from '../components/ui';
import {
  dashboardAPI, partiesAPI, soAPI, poAPI, containerAPI,
  invoiceAPI, billAPI, productAPI, grnAPI, sbAPI, reportsAPI
} from '../utils/api';

// ── HOOK ─────────────────────────────────────────────────
function useData(fetchFn, deps=[]) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    fetchFn().then(r => setData(r.data)).catch(() => toast.error('Load failed')).finally(() => setLoading(false));
  }, deps);
  useEffect(() => { reload(); }, [reload]);
  return [data, loading, reload];
}

// ══════════════════════════════════════════════════════════
// DASHBOARD
// ══════════════════════════════════════════════════════════
export function Dashboard() {
  const [data, loading] = useData(() => dashboardAPI.get());
  if (loading) return <Spinner />;
  const s = data?.stats || {};
  const so = data?.recent_so || [];

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-black text-slate-800">📊 Dashboard</h1>
        <p className="text-sm text-slate-400 mt-0.5">FY 2025–26 — Real-time trade overview</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard icon="🛒" label="Sales Orders" value={s.total_so || 0} sub="Active" color="blue" />
        <StatCard icon="📦" label="Purchase Orders" value={s.total_po || 0} sub="Placed" color="purple" />
        <StatCard icon="🚢" label="Containers" value={s.active_containers || 0} sub="In transit" color="amber" />
        <StatCard icon="🧾" label="Pending Invoices" value={s.pending_invoices || 0} sub="Awaiting payment" color="red" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatCard icon="💹" label="Export Revenue (YTD)" value={fmtAmt(s.export_revenue)} color="green" />
        <StatCard icon="📑" label="Bills Payable" value={fmtAmt(s.pending_bills)} color="red" />
        <StatCard icon="🧮" label="ITC Available (Month)" value={fmtAmt(s.itc_available)} color="blue" />
      </div>
      <Card>
        <CardHeader title="Recent Sales Orders" actions={<Btn size="sm" variant="outline" as="a" href="/sales-orders">View All</Btn>} />
        <Table headers={['SO No.','Date','Buyer','Value','Status']}>
          {so.map(r => (
            <Tr key={r.so_number}>
              <Td><span className="font-bold text-blue-600">{r.so_number}</span></Td>
              <Td>{fmtDate(r.so_date)}</Td>
              <Td>{r.buyer_name}</Td>
              <Td right>{r.currency} {parseFloat(r.total_amount).toLocaleString()}</Td>
              <Td><StatusBadge status={r.status} /></Td>
            </Tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PARTIES
// ══════════════════════════════════════════════════════════
const EMPTY_PARTY = { party_type:'buyer', name:'', country:'India', currency:'INR', gstin:'', vat_no:'', address:'', contact_person:'', email:'', phone:'', credit_days:30, credit_limit:0, bank_name:'', bank_account:'', bank_ifsc:'', bank_swift:'', notes:'' };

export function Parties() {
  const [data, loading, reload] = useData(() => partiesAPI.list());
  const [modal, setModal]   = useState(false);
  const [form, setForm]     = useState(EMPTY_PARTY);
  const [saving, setSaving] = useState(false);
  const [tab, setTab]       = useState('all');

  const parties = (data?.parties || []).filter(p => tab === 'all' || p.party_type === tab);

  const save = async () => {
    if (!form.name || !form.party_type) return toast.error('Name and type required');
    setSaving(true);
    try {
      if (form.id) { await partiesAPI.update(form.id, form); toast.success('Party updated'); }
      else { await partiesAPI.create(form); toast.success('Party added'); }
      setModal(false); reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const edit = p => { setForm(p); setModal(true); };
  const add  = () => { setForm(EMPTY_PARTY); setModal(true); };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">🏢 Parties</h1><p className="text-sm text-slate-400">Buyers, Suppliers, CHA, Banks</p></div>
        <div className="ml-auto"><Btn onClick={add}>+ Add Party</Btn></div>
      </div>
      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-lg w-fit">
        {['all','buyer','supplier','cha','bank'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${tab === t ? 'bg-white shadow text-blue-600' : 'text-slate-500'}`}>
            {t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['Name','Type','Country','GSTIN/VAT','Contact','Email','Credit','Actions']}>
            {parties.map(p => (
              <Tr key={p.id}>
                <Td><span className="font-semibold text-slate-800">{p.name}</span></Td>
                <Td><Badge color={p.party_type==='buyer'?'blue':p.party_type==='supplier'?'purple':'gray'}>{p.party_type}</Badge></Td>
                <Td>{p.country}</Td>
                <Td mono>{p.gstin || p.vat_no || '—'}</Td>
                <Td>{p.contact_person || '—'}</Td>
                <Td>{p.email || '—'}</Td>
                <Td>{p.credit_days ? `${p.credit_days}d` : '—'}</Td>
                <Td>
                  <Btn size="sm" variant="ghost" onClick={() => edit(p)}>Edit</Btn>
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={form.id ? 'Edit Party' : 'Add Party'} size="lg"
        footer={<><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>Save Party</Btn></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup label="Party Type" required>
            <Select value={form.party_type} onChange={e => setForm(f=>({...f,party_type:e.target.value}))}>
              <option value="buyer">Buyer (Importer)</option>
              <option value="supplier">Supplier</option>
              <option value="cha">CHA</option>
              <option value="bank">Bank</option>
              <option value="agent">Agent</option>
            </Select>
          </FormGroup>
          <FormGroup label="Party Name" required>
            <Input placeholder="Legal company name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} />
          </FormGroup>
          <FormGroup label="Country">
            <Input value={form.country} onChange={e=>setForm(f=>({...f,country:e.target.value}))} />
          </FormGroup>
          <FormGroup label="Currency">
            <Select value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))}>
              <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option><option>AED</option>
            </Select>
          </FormGroup>
          <FormGroup label="GSTIN"><Input value={form.gstin} onChange={e=>setForm(f=>({...f,gstin:e.target.value}))} placeholder="27AAACM1234F1ZX" /></FormGroup>
          <FormGroup label="VAT / EIN / TRN"><Input value={form.vat_no} onChange={e=>setForm(f=>({...f,vat_no:e.target.value}))} /></FormGroup>
          <FormGroup label="Contact Person"><Input value={form.contact_person} onChange={e=>setForm(f=>({...f,contact_person:e.target.value}))} /></FormGroup>
          <FormGroup label="Email"><Input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} /></FormGroup>
          <FormGroup label="Phone"><Input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} /></FormGroup>
          <FormGroup label="Credit Days"><Input type="number" value={form.credit_days} onChange={e=>setForm(f=>({...f,credit_days:e.target.value}))} /></FormGroup>
          <FormGroup label="Bank Name"><Input value={form.bank_name} onChange={e=>setForm(f=>({...f,bank_name:e.target.value}))} /></FormGroup>
          <FormGroup label="Bank A/C"><Input value={form.bank_account} onChange={e=>setForm(f=>({...f,bank_account:e.target.value}))} /></FormGroup>
          <FormGroup label="IFSC"><Input value={form.bank_ifsc} onChange={e=>setForm(f=>({...f,bank_ifsc:e.target.value}))} /></FormGroup>
          <FormGroup label="SWIFT"><Input value={form.bank_swift} onChange={e=>setForm(f=>({...f,bank_swift:e.target.value}))} /></FormGroup>
        </div>
        <div className="mt-3">
          <FormGroup label="Address"><Textarea value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))} /></FormGroup>
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// SALES ORDERS
// ══════════════════════════════════════════════════════════
const emptyItem = () => ({ description:'', hs_code:'', qty:'', uom:'MTR', unit_price:'', amount:0 });

export function SalesOrders() {
  const [data, loading, reload] = useData(() => soAPI.list());
  const [partiesData] = useData(() => partiesAPI.list({ type:'buyer' }));
  const [modal, setModal]   = useState(false);
  const [form, setForm]     = useState({ party_id:'', so_date:new Date().toISOString().split('T')[0], incoterms:'CIF', currency:'USD', payment_terms:'LC 90 Days', items:[emptyItem()] });
  const [saving, setSaving] = useState(false);

  const buyers = partiesData?.parties || [];
  const orders = data?.sales_orders || [];

  const setItem = (i, field, val) => {
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], [field]: val };
      if (field==='qty'||field==='unit_price') {
        items[i].amount = parseFloat(items[i].qty||0) * parseFloat(items[i].unit_price||0);
      }
      return { ...f, items };
    });
  };

  const total = form.items.reduce((s,it) => s + parseFloat(it.amount||0), 0);

  const save = async () => {
    if (!form.party_id) return toast.error('Select buyer');
    if (!form.items[0].description) return toast.error('Add at least one item');
    setSaving(true);
    try {
      await soAPI.create({ ...form, items: form.items.map(it=>({...it, amount: parseFloat(it.qty)*parseFloat(it.unit_price)})) });
      toast.success('Sales Order created!');
      setModal(false);
      reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">🛒 Sales Orders</h1><p className="text-sm text-slate-400">Orders received from buyers</p></div>
        <div className="ml-auto"><Btn onClick={()=>{setForm({party_id:'',so_date:new Date().toISOString().split('T')[0],incoterms:'CIF',currency:'USD',payment_terms:'LC 90 Days',items:[emptyItem()]});setModal(true);}}>+ New Sales Order</Btn></div>
      </div>
      <Alert type="info">💡 <strong>Flow:</strong> Create SO → System links PO → Book Container → Create Invoice → Collect Payment</Alert>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['SO No.','Date','Buyer','Items','Value','Incoterms','PO Status','Status','Actions']}>
            {orders.map(r => (
              <Tr key={r.id}>
                <Td><span className="font-bold text-blue-600 font-mono text-xs">{r.so_number}</span></Td>
                <Td>{fmtDate(r.so_date)}</Td>
                <Td><span className="font-semibold">{r.party_name}</span><br/><span className="text-xs text-slate-400">{r.country}</span></Td>
                <Td>—</Td>
                <Td right><span className="font-bold">{r.currency} {parseFloat(r.total_amount).toLocaleString()}</span></Td>
                <Td>{r.incoterms}</Td>
                <Td>{r.status==='po_raised'||r.status==='shipped'||r.status==='invoiced' ? <Badge color="green">PO ✓</Badge> : <Badge color="yellow">PO Pending</Badge>}</Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td>
                  <div className="flex gap-1">
                    <Btn size="sm" variant="ghost">View</Btn>
                    {r.status==='confirmed' && <Btn size="sm" variant="success">➜ PO</Btn>}
                  </div>
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title="New Sales Order" size="xl"
        footer={<><Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>✓ Confirm SO</Btn></>}>
        <SectionDivider title="Buyer & Terms" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
          <FormGroup label="Buyer" required>
            <Select value={form.party_id} onChange={e=>setForm(f=>({...f,party_id:e.target.value}))}>
              <option value="">— Select Buyer —</option>
              {buyers.map(b=><option key={b.id} value={b.id}>{b.name} ({b.country})</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="SO Date"><Input type="date" value={form.so_date} onChange={e=>setForm(f=>({...f,so_date:e.target.value}))} /></FormGroup>
          <FormGroup label="Buyer PO Ref"><Input value={form.buyer_po_ref||''} onChange={e=>setForm(f=>({...f,buyer_po_ref:e.target.value}))} /></FormGroup>
          <FormGroup label="Incoterms">
            <Select value={form.incoterms} onChange={e=>setForm(f=>({...f,incoterms:e.target.value}))}>
              <option>CIF</option><option>FOB</option><option>CFR</option><option>EXW</option><option>DAP</option>
            </Select>
          </FormGroup>
          <FormGroup label="Currency">
            <Select value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))}>
              <option>USD</option><option>EUR</option><option>GBP</option><option>AED</option><option>INR</option>
            </Select>
          </FormGroup>
          <FormGroup label="Payment Terms">
            <Select value={form.payment_terms} onChange={e=>setForm(f=>({...f,payment_terms:e.target.value}))}>
              <option>LC 90 Days Sight</option><option>LC at Sight</option><option>TT 30 Days</option><option>TT Advance</option><option>DA 60 Days</option>
            </Select>
          </FormGroup>
          <FormGroup label="Port of Loading"><Input value={form.port_of_loading||''} onChange={e=>setForm(f=>({...f,port_of_loading:e.target.value}))} placeholder="JNPT, Mumbai" /></FormGroup>
          <FormGroup label="Port of Destination"><Input value={form.port_of_dest||''} onChange={e=>setForm(f=>({...f,port_of_dest:e.target.value}))} placeholder="Hamburg, Germany" /></FormGroup>
          <FormGroup label="Shipment Date"><Input type="date" value={form.shipment_date||''} onChange={e=>setForm(f=>({...f,shipment_date:e.target.value}))} /></FormGroup>
        </div>

        <SectionDivider title="Line Items" />
        <div className="overflow-x-auto mb-3">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="bg-blue-600 text-white">
              <th className="px-3 py-2 text-left text-xs font-semibold">Description</th>
              <th className="px-2 py-2 text-xs font-semibold w-24">HS Code</th>
              <th className="px-2 py-2 text-xs font-semibold w-20">Qty</th>
              <th className="px-2 py-2 text-xs font-semibold w-20">UoM</th>
              <th className="px-2 py-2 text-xs font-semibold w-24">Unit Price</th>
              <th className="px-2 py-2 text-right text-xs font-semibold w-28">Amount</th>
              <th className="w-8"></th>
            </tr></thead>
            <tbody>
              {form.items.map((it,i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-1 py-1"><Input value={it.description} onChange={e=>setItem(i,'description',e.target.value)} placeholder="Product / goods" /></td>
                  <td className="px-1 py-1"><Input value={it.hs_code} onChange={e=>setItem(i,'hs_code',e.target.value)} placeholder="5208.12" /></td>
                  <td className="px-1 py-1"><Input type="number" value={it.qty} onChange={e=>setItem(i,'qty',e.target.value)} placeholder="0" /></td>
                  <td className="px-1 py-1"><Select value={it.uom} onChange={e=>setItem(i,'uom',e.target.value)}><option>MTR</option><option>KG</option><option>MT</option><option>PCS</option><option>SET</option></Select></td>
                  <td className="px-1 py-1"><Input type="number" value={it.unit_price} onChange={e=>setItem(i,'unit_price',e.target.value)} placeholder="0.00" /></td>
                  <td className="px-3 py-1 text-right font-bold text-blue-600">{parseFloat(it.amount||0).toFixed(2)}</td>
                  <td className="px-1 py-1"><button className="text-red-400 hover:text-red-600 text-sm" onClick={()=>setForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}))}>✕</button></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="bg-blue-50"><td colSpan={5} className="px-3 py-2 text-right font-bold text-blue-700 text-sm">Total</td><td className="px-3 py-2 text-right font-black text-blue-700">{total.toFixed(2)}</td><td></td></tr></tfoot>
          </table>
        </div>
        <Btn variant="ghost" size="sm" onClick={()=>setForm(f=>({...f,items:[...f.items,emptyItem()]}))}>+ Add Line</Btn>

        <div className="mt-4">
          <FormGroup label="Remarks"><Textarea value={form.remarks||''} onChange={e=>setForm(f=>({...f,remarks:e.target.value}))} /></FormGroup>
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PURCHASE ORDERS
// ══════════════════════════════════════════════════════════
const emptyPOItem = () => ({ description:'', hsn_code:'', qty:'', uom:'MTR', unit_rate:'', gst_rate:5, taxable_amount:0, gst_amount:0, total_amount:0 });

export function PurchaseOrders() {
  const [data, loading, reload] = useData(() => poAPI.list());
  const [soData] = useData(() => soAPI.list({ status:'confirmed' }));
  const [supplierData] = useData(() => partiesAPI.list({ type:'supplier' }));
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ party_id:'', so_id:'', po_date:new Date().toISOString().split('T')[0], payment_terms:'Net 30', items:[emptyPOItem()] });
  const [saving, setSaving] = useState(false);

  const orders = data?.purchase_orders || [];
  const suppliers = supplierData?.parties || [];
  const sos = soData?.sales_orders || [];

  const setPOItem = (i,field,val) => {
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], [field]:val };
      const qty = parseFloat(items[i].qty||0);
      const rate = parseFloat(items[i].unit_rate||0);
      const gstR = parseFloat(items[i].gst_rate||5);
      items[i].taxable_amount = qty * rate;
      items[i].gst_amount = items[i].taxable_amount * gstR / 100;
      items[i].total_amount = items[i].taxable_amount + items[i].gst_amount;
      return { ...f, items };
    });
  };

  const grandTotal = form.items.reduce((s,it)=>s+parseFloat(it.total_amount||0),0);

  const save = async () => {
    if (!form.party_id) return toast.error('Select supplier');
    setSaving(true);
    try {
      await poAPI.create(form);
      toast.success('Purchase Order placed!');
      setModal(false); reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">📦 Purchase Orders</h1><p className="text-sm text-slate-400">Orders placed to suppliers</p></div>
        <div className="ml-auto"><Btn variant="success" onClick={()=>{setForm({party_id:'',so_id:'',po_date:new Date().toISOString().split('T')[0],payment_terms:'Net 30',items:[emptyPOItem()]});setModal(true);}}>+ New Purchase Order</Btn></div>
      </div>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['PO No.','Date','Supplier','Linked SO','Taxable','GST','Total','Status','Actions']}>
            {orders.map(r=>(
              <Tr key={r.id}>
                <Td><span className="font-bold text-emerald-600 font-mono text-xs">{r.po_number}</span></Td>
                <Td>{fmtDate(r.po_date)}</Td>
                <Td><span className="font-semibold">{r.supplier_name}</span></Td>
                <Td>{r.so_number ? <Badge color="blue">{r.so_number}</Badge> : '—'}</Td>
                <Td right>{fmtAmt(r.total_taxable)}</Td>
                <Td right>{fmtAmt(r.total_gst)}</Td>
                <Td right><span className="font-bold">{fmtAmt(r.grand_total)}</span></Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td>
                  <div className="flex gap-1">
                    <Btn size="sm" variant="ghost">View</Btn>
                    {r.status==='grn_done' && <Btn size="sm" variant="primary">➜ Bill</Btn>}
                  </div>
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={()=>setModal(false)} title="New Purchase Order" size="xl"
        footer={<><Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn><Btn variant="success" loading={saving} onClick={save}>✓ Place PO</Btn></>}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
          <FormGroup label="Supplier" required>
            <Select value={form.party_id} onChange={e=>setForm(f=>({...f,party_id:e.target.value}))}>
              <option value="">— Select Supplier —</option>
              {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="PO Date"><Input type="date" value={form.po_date} onChange={e=>setForm(f=>({...f,po_date:e.target.value}))} /></FormGroup>
          <FormGroup label="Linked Sales Order">
            <Select value={form.so_id} onChange={e=>setForm(f=>({...f,so_id:e.target.value}))}>
              <option value="">— Select SO (optional) —</option>
              {sos.map(s=><option key={s.id} value={s.id}>{s.so_number} — {s.party_name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Delivery Date"><Input type="date" value={form.delivery_date||''} onChange={e=>setForm(f=>({...f,delivery_date:e.target.value}))} /></FormGroup>
          <FormGroup label="Payment Terms">
            <Select value={form.payment_terms} onChange={e=>setForm(f=>({...f,payment_terms:e.target.value}))}>
              <option>Net 30</option><option>Net 45</option><option>Advance 100%</option><option>50% Advance + 50% Delivery</option>
            </Select>
          </FormGroup>
          <FormGroup label="Delivery Address"><Input value={form.delivery_address||''} onChange={e=>setForm(f=>({...f,delivery_address:e.target.value}))} placeholder="Warehouse / Port" /></FormGroup>
        </div>
        <SectionDivider title="Line Items" />
        <div className="overflow-x-auto mb-3">
          <table className="w-full text-sm min-w-[800px]">
            <thead><tr className="bg-emerald-600 text-white">
              <th className="px-3 py-2 text-left text-xs font-semibold">Item Description</th>
              <th className="px-2 py-2 text-xs w-20">HSN</th>
              <th className="px-2 py-2 text-xs w-16">Qty</th>
              <th className="px-2 py-2 text-xs w-16">UoM</th>
              <th className="px-2 py-2 text-xs w-22">Rate (₹)</th>
              <th className="px-2 py-2 text-xs w-24">Taxable (₹)</th>
              <th className="px-2 py-2 text-xs w-16">GST%</th>
              <th className="px-2 py-2 text-xs w-20">GST (₹)</th>
              <th className="px-2 py-2 text-right text-xs w-24">Total (₹)</th>
              <th className="w-8"></th>
            </tr></thead>
            <tbody>
              {form.items.map((it,i)=>(
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-1 py-1"><Input value={it.description} onChange={e=>setPOItem(i,'description',e.target.value)} /></td>
                  <td className="px-1 py-1"><Input value={it.hsn_code} onChange={e=>setPOItem(i,'hsn_code',e.target.value)} /></td>
                  <td className="px-1 py-1"><Input type="number" value={it.qty} onChange={e=>setPOItem(i,'qty',e.target.value)} /></td>
                  <td className="px-1 py-1"><Select value={it.uom} onChange={e=>setPOItem(i,'uom',e.target.value)}><option>MTR</option><option>KG</option><option>MT</option><option>PCS</option></Select></td>
                  <td className="px-1 py-1"><Input type="number" value={it.unit_rate} onChange={e=>setPOItem(i,'unit_rate',e.target.value)} /></td>
                  <td className="px-2 py-1 text-right font-semibold">{parseFloat(it.taxable_amount).toFixed(2)}</td>
                  <td className="px-1 py-1"><Select value={it.gst_rate} onChange={e=>setPOItem(i,'gst_rate',e.target.value)}><option value="5">5%</option><option value="12">12%</option><option value="18">18%</option><option value="28">28%</option></Select></td>
                  <td className="px-2 py-1 text-right text-green-600 font-semibold">{parseFloat(it.gst_amount).toFixed(2)}</td>
                  <td className="px-2 py-1 text-right font-black text-emerald-700">{parseFloat(it.total_amount).toFixed(2)}</td>
                  <td><button className="text-red-400 text-sm" onClick={()=>setForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}))}>✕</button></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr className="bg-emerald-50"><td colSpan={8} className="px-3 py-2 text-right font-bold text-emerald-700">Grand Total</td><td className="px-2 py-2 text-right font-black text-emerald-700">{fmtAmt(grandTotal)}</td><td></td></tr></tfoot>
          </table>
        </div>
        <Btn variant="ghost" size="sm" onClick={()=>setForm(f=>({...f,items:[...f.items,emptyPOItem()]}))}>+ Add Item</Btn>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// CONTAINERS
// ══════════════════════════════════════════════════════════
export function Containers() {
  const [data, loading, reload] = useData(() => containerAPI.list());
  const [soData] = useData(() => soAPI.list());
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ so_id:'', container_number:'', seal_number:'', container_type:'20 FCL', carrier:'', vessel_name:'', voyage_number:'', pol:'JNPT, Nhava Sheva', pod:'', etd:'', eta:'', freight_terms:'prepaid', freight_amount:'', freight_currency:'USD', bl_number:'' });
  const [saving, setSaving] = useState(false);

  const containers = data?.containers || [];
  const sos = soData?.sales_orders || [];

  const save = async () => {
    if (!form.container_number) return toast.error('Container number required');
    setSaving(true);
    try {
      await containerAPI.create(form);
      toast.success('Container booked!');
      setModal(false); reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const statusColors = { booked:'blue', stuffed:'purple', leo:'green', sailed:'blue', in_transit:'blue', arrived:'amber', delivered:'green' };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">🚢 Containers</h1><p className="text-sm text-slate-400">Book & track containers linked to Sales Orders</p></div>
        <div className="ml-auto"><Btn onClick={()=>setModal(true)}>+ Book Container</Btn></div>
      </div>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['Container No.','Seal','Type','Carrier','Vessel','POL → POD','ETD','ETA','BL No.','SO','Status']}>
            {containers.map(c=>(
              <Tr key={c.id}>
                <Td><span className="font-mono font-bold text-blue-600">{c.container_number}</span></Td>
                <Td mono>{c.seal_number||'—'}</Td>
                <Td>{c.container_type}</Td>
                <Td>{c.carrier}</Td>
                <Td><span className="font-semibold">{c.vessel_name}</span></Td>
                <Td>{c.pol} → {c.pod}</Td>
                <Td>{fmtDate(c.etd)}</Td>
                <Td>{fmtDate(c.eta)}</Td>
                <Td mono>{c.bl_number||'—'}</Td>
                <Td>{c.so_number?<Badge color="blue">{c.so_number}</Badge>:'—'}</Td>
                <Td><StatusBadge status={c.status} /></Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={()=>setModal(false)} title="Book Container" size="lg"
        footer={<><Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>✓ Save Container</Btn></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup label="Linked Sales Order">
            <Select value={form.so_id} onChange={e=>setForm(f=>({...f,so_id:e.target.value}))}>
              <option value="">— Select SO —</option>
              {sos.map(s=><option key={s.id} value={s.id}>{s.so_number} — {s.party_name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Container Number" required><Input value={form.container_number} onChange={e=>setForm(f=>({...f,container_number:e.target.value.toUpperCase()}))} placeholder="e.g. HLCU4489221" /></FormGroup>
          <FormGroup label="Seal Number"><Input value={form.seal_number} onChange={e=>setForm(f=>({...f,seal_number:e.target.value}))} /></FormGroup>
          <FormGroup label="Container Type">
            <Select value={form.container_type} onChange={e=>setForm(f=>({...f,container_type:e.target.value}))}>
              <option>20 FCL</option><option>40 FCL</option><option>40 HC</option><option>LCL</option>
            </Select>
          </FormGroup>
          <FormGroup label="Carrier">
            <Select value={form.carrier} onChange={e=>setForm(f=>({...f,carrier:e.target.value}))}>
              <option>Hapag-Lloyd</option><option>MSC</option><option>COSCO</option><option>Evergreen</option><option>ONE</option><option>Maersk</option><option>CMA CGM</option>
            </Select>
          </FormGroup>
          <FormGroup label="Vessel Name"><Input value={form.vessel_name} onChange={e=>setForm(f=>({...f,vessel_name:e.target.value}))} /></FormGroup>
          <FormGroup label="Voyage Number"><Input value={form.voyage_number} onChange={e=>setForm(f=>({...f,voyage_number:e.target.value}))} /></FormGroup>
          <FormGroup label="Port of Loading (POL)">
            <Select value={form.pol} onChange={e=>setForm(f=>({...f,pol:e.target.value}))}>
              <option>JNPT, Nhava Sheva</option><option>Chennai</option><option>Mundra</option><option>Kolkata</option><option>Cochin</option>
            </Select>
          </FormGroup>
          <FormGroup label="Port of Discharge (POD)"><Input value={form.pod} onChange={e=>setForm(f=>({...f,pod:e.target.value}))} /></FormGroup>
          <FormGroup label="ETD"><Input type="date" value={form.etd} onChange={e=>setForm(f=>({...f,etd:e.target.value}))} /></FormGroup>
          <FormGroup label="ETA"><Input type="date" value={form.eta} onChange={e=>setForm(f=>({...f,eta:e.target.value}))} /></FormGroup>
          <FormGroup label="Freight Terms"><Select value={form.freight_terms} onChange={e=>setForm(f=>({...f,freight_terms:e.target.value}))}><option value="prepaid">Prepaid</option><option value="collect">Collect</option></Select></FormGroup>
          <FormGroup label="Freight Amount"><Input type="number" value={form.freight_amount} onChange={e=>setForm(f=>({...f,freight_amount:e.target.value}))} /></FormGroup>
          <FormGroup label="B/L Number" hint="Fill after vessel sails"><Input value={form.bl_number} onChange={e=>setForm(f=>({...f,bl_number:e.target.value}))} /></FormGroup>
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// SALES INVOICES
// ══════════════════════════════════════════════════════════
export function Invoices() {
  const [data, loading, reload] = useData(() => invoiceAPI.list());
  const [soData] = useData(() => soAPI.list({ status:'shipped' }));
  const [containerData] = useData(() => containerAPI.list());
  const [partiesData] = useData(() => partiesAPI.list({ type:'buyer' }));
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ so_id:'', container_id:'', party_id:'', invoice_date:new Date().toISOString().split('T')[0], currency:'USD', exchange_rate:'83.42', incoterms:'CIF', payment_terms:'LC 90 Days', gst_type:'zero_lut' });
  const [saving, setSaving] = useState(false);

  const invoices = data?.invoices || [];

  const save = async () => {
    if (!form.so_id || !form.party_id) return toast.error('SO and Party required');
    setSaving(true);
    try {
      await invoiceAPI.create(form);
      toast.success('Invoice created!');
      setModal(false); reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">🧾 Sales Invoices</h1></div>
        <div className="ml-auto"><Btn onClick={()=>setModal(true)}>+ Create Invoice</Btn></div>
      </div>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['Invoice No.','Date','Buyer','SO','Container','Amount','GST','Due Date','Payment','BRC','Status','Actions']}>
            {invoices.map(r=>(
              <Tr key={r.id}>
                <Td><span className="font-bold text-blue-600 font-mono text-xs">{r.invoice_number}</span></Td>
                <Td>{fmtDate(r.invoice_date)}</Td>
                <Td><span className="font-semibold">{r.party_name}</span></Td>
                <Td>{r.so_number?<Badge color="blue">{r.so_number}</Badge>:'—'}</Td>
                <Td mono>{r.container_number||'—'}</Td>
                <Td right><span className="font-bold">{r.currency} {parseFloat(r.grand_total).toLocaleString()}</span></Td>
                <Td>{r.gst_type==='zero_lut'?<Badge color="green">ZERO LUT</Badge>:<Badge color="blue">IGST</Badge>}</Td>
                <Td>{fmtDate(r.due_date)}</Td>
                <Td><StatusBadge status={r.payment_status} /></Td>
                <Td>{r.brc_status==='filed'?<Badge color="green">e-BRC ✓</Badge>:<Badge color="yellow">BRC Pending</Badge>}</Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td><Btn size="sm" variant="ghost">View</Btn></Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={()=>setModal(false)} title="Create Sales Invoice" size="lg"
        footer={<><Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>✓ Create Invoice</Btn></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup label="Sales Order" required>
            <Select value={form.so_id} onChange={e=>setForm(f=>({...f,so_id:e.target.value}))}>
              <option value="">— Select SO —</option>
              {(soData?.sales_orders||[]).map(s=><option key={s.id} value={s.id}>{s.so_number} — {s.party_name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Buyer" required>
            <Select value={form.party_id} onChange={e=>setForm(f=>({...f,party_id:e.target.value}))}>
              <option value="">— Select Buyer —</option>
              {(partiesData?.parties||[]).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Container">
            <Select value={form.container_id} onChange={e=>setForm(f=>({...f,container_id:e.target.value}))}>
              <option value="">— Select Container —</option>
              {(containerData?.containers||[]).map(c=><option key={c.id} value={c.id}>{c.container_number} — {c.vessel_name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Invoice Date"><Input type="date" value={form.invoice_date} onChange={e=>setForm(f=>({...f,invoice_date:e.target.value}))} /></FormGroup>
          <FormGroup label="Currency"><Select value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))}><option>USD</option><option>EUR</option><option>GBP</option><option>AED</option></Select></FormGroup>
          <FormGroup label="Exchange Rate (₹)"><Input type="number" value={form.exchange_rate} onChange={e=>setForm(f=>({...f,exchange_rate:e.target.value}))} /></FormGroup>
          <FormGroup label="GST Treatment">
            <Select value={form.gst_type} onChange={e=>setForm(f=>({...f,gst_type:e.target.value}))}>
              <option value="zero_lut">Zero Rated (LUT)</option>
              <option value="zero_bond">Zero Rated (Bond)</option>
              <option value="igst">IGST on Export</option>
            </Select>
          </FormGroup>
          <FormGroup label="Shipping Bill No."><Input value={form.sb_number||''} onChange={e=>setForm(f=>({...f,sb_number:e.target.value}))} /></FormGroup>
          <FormGroup label="B/L Number"><Input value={form.bl_number||''} onChange={e=>setForm(f=>({...f,bl_number:e.target.value}))} /></FormGroup>
          <FormGroup label="LC Number"><Input value={form.lc_number||''} onChange={e=>setForm(f=>({...f,lc_number:e.target.value}))} /></FormGroup>
          <FormGroup label="Due Date"><Input type="date" value={form.due_date||''} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))} /></FormGroup>
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PURCHASE BILLS
// ══════════════════════════════════════════════════════════
export function PurchaseBills() {
  const [data, loading, reload] = useData(() => billAPI.list());
  const [supplierData] = useData(() => partiesAPI.list({ type:'supplier' }));
  const [poData] = useData(() => poAPI.list({ status:'grn_done' }));
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ bill_number:'', bill_date:new Date().toISOString().split('T')[0], party_id:'', po_id:'', taxable_amount:'', gst_rate:'5', tds_rate:'0', supplier_gstin:'', due_date:'' });
  const [saving, setSaving] = useState(false);

  const bills = data?.bills || [];
  const taxable = parseFloat(form.taxable_amount||0);
  const gstAmt  = taxable * parseFloat(form.gst_rate) / 100;
  const tdsAmt  = taxable * parseFloat(form.tds_rate||0) / 100;
  const total   = taxable + gstAmt - tdsAmt;

  const save = async () => {
    if (!form.bill_number || !form.party_id) return toast.error('Bill number and supplier required');
    setSaving(true);
    try {
      await billAPI.create({ ...form, tds_amount: tdsAmt });
      toast.success('Purchase Bill entered — ITC booked!');
      setModal(false); reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">📑 Purchase Bills</h1><p className="text-sm text-slate-400">Supplier invoices — enter to claim ITC</p></div>
        <div className="ml-auto"><Btn variant="success" onClick={()=>setModal(true)}>+ Enter Purchase Bill</Btn></div>
      </div>
      <Alert type="info">💡 ITC is auto-booked on saving. Reconcile with GSTR-2B monthly.</Alert>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={["Supplier's Bill No.",'Date','Supplier','PO Ref','Taxable','GST (ITC)','Total','Due Date','TDS','Status','Action']}>
            {bills.map(r=>(
              <Tr key={r.id}>
                <Td><span className="font-bold font-mono text-xs">{r.bill_number}</span></Td>
                <Td>{fmtDate(r.bill_date)}</Td>
                <Td><span className="font-semibold">{r.supplier_name}</span></Td>
                <Td>{r.po_number?<Badge color="purple">{r.po_number}</Badge>:'—'}</Td>
                <Td right>{fmtAmt(r.taxable_amount)}</Td>
                <Td right><span className="text-emerald-600 font-bold">{fmtAmt(r.gst_amount)} ✓</span></Td>
                <Td right><span className="font-bold">{fmtAmt(r.total_amount)}</span></Td>
                <Td>{fmtDate(r.due_date)}</Td>
                <Td right>{r.tds_amount>0?fmtAmt(r.tds_amount):'—'}</Td>
                <Td><StatusBadge status={r.payment_status} /></Td>
                <Td>{r.payment_status!=='paid'&&<Btn size="sm" variant="primary">Pay</Btn>}</Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={()=>setModal(false)} title="Enter Purchase Bill" size="lg"
        footer={<><Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn><Btn variant="success" loading={saving} onClick={save}>✓ Save Bill</Btn></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup label="Supplier" required>
            <Select value={form.party_id} onChange={e=>setForm(f=>({...f,party_id:e.target.value}))}>
              <option value="">— Select Supplier —</option>
              {(supplierData?.parties||[]).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Supplier's Invoice No." required><Input value={form.bill_number} onChange={e=>setForm(f=>({...f,bill_number:e.target.value}))} /></FormGroup>
          <FormGroup label="Invoice Date"><Input type="date" value={form.bill_date} onChange={e=>setForm(f=>({...f,bill_date:e.target.value}))} /></FormGroup>
          <FormGroup label="Linked PO">
            <Select value={form.po_id} onChange={e=>setForm(f=>({...f,po_id:e.target.value}))}>
              <option value="">— Select PO —</option>
              {(poData?.purchase_orders||[]).map(p=><option key={p.id} value={p.id}>{p.po_number} — {p.supplier_name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Taxable Amount (₹)" required><Input type="number" value={form.taxable_amount} onChange={e=>setForm(f=>({...f,taxable_amount:e.target.value}))} /></FormGroup>
          <FormGroup label="GST Rate">
            <Select value={form.gst_rate} onChange={e=>setForm(f=>({...f,gst_rate:e.target.value}))}>
              <option value="5">5%</option><option value="12">12%</option><option value="18">18%</option><option value="28">28%</option>
            </Select>
          </FormGroup>
          <FormGroup label="GST Amount (Auto)"><Input readOnly value={fmtAmt(gstAmt)} className="bg-slate-100" /></FormGroup>
          <FormGroup label="TDS Rate %"><Input type="number" value={form.tds_rate} onChange={e=>setForm(f=>({...f,tds_rate:e.target.value}))} /></FormGroup>
          <FormGroup label="TDS Amount (Auto)"><Input readOnly value={fmtAmt(tdsAmt)} className="bg-slate-100" /></FormGroup>
          <FormGroup label="Net Total (₹)"><Input readOnly value={fmtAmt(total)} className="bg-emerald-50 font-bold text-emerald-700" /></FormGroup>
          <FormGroup label="Supplier GSTIN"><Input value={form.supplier_gstin} onChange={e=>setForm(f=>({...f,supplier_gstin:e.target.value}))} /></FormGroup>
          <FormGroup label="Due Date"><Input type="date" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))} /></FormGroup>
        </div>
        <Alert type="success">ITC of <strong>{fmtAmt(gstAmt)}</strong> will be auto-claimed in GSTR-3B</Alert>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PRODUCTS
// ══════════════════════════════════════════════════════════
export function Products() {
  const [data, loading, reload] = useData(() => productAPI.list());
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ code:'', name:'', hs_code:'', uom:'KG', gst_rate:5, bcd_rate:0, drawback_rate:0, rodtep_rate:0 });
  const [saving, setSaving] = useState(false);
  const products = data?.products || [];
  const save = async () => {
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try { await productAPI.create(form); toast.success('Product added'); setModal(false); reload(); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">🔧 Products & HS Codes</h1></div>
        <div className="ml-auto"><Btn onClick={()=>setModal(true)}>+ Add Product</Btn></div>
      </div>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['Code','Name','HS Code','UoM','GST%','BCD%','Drawback%','RoDTEP%','Restricted']}>
            {products.map(p=>(
              <Tr key={p.id}>
                <Td mono>{p.code||'—'}</Td>
                <Td><span className="font-semibold">{p.name}</span></Td>
                <Td mono>{p.hs_code}</Td>
                <Td>{p.uom}</Td>
                <Td>{p.gst_rate}%</Td>
                <Td>{p.bcd_rate}%</Td>
                <Td>{p.drawback_rate}%</Td>
                <Td>{p.rodtep_rate}%</Td>
                <Td>{p.is_restricted?<Badge color="red">Restricted</Badge>:<Badge color="green">Free</Badge>}</Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>
      <Modal open={modal} onClose={()=>setModal(false)} title="Add Product" size="md"
        footer={<><Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>Save</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Product Code"><Input value={form.code} onChange={e=>setForm(f=>({...f,code:e.target.value}))} /></FormGroup>
          <FormGroup label="Product Name" required><Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></FormGroup>
          <FormGroup label="HS Code"><Input value={form.hs_code} onChange={e=>setForm(f=>({...f,hs_code:e.target.value}))} /></FormGroup>
          <FormGroup label="UoM"><Select value={form.uom} onChange={e=>setForm(f=>({...f,uom:e.target.value}))}><option>KG</option><option>MTR</option><option>MT</option><option>PCS</option><option>SET</option></Select></FormGroup>
          <FormGroup label="GST Rate %"><Input type="number" value={form.gst_rate} onChange={e=>setForm(f=>({...f,gst_rate:e.target.value}))} /></FormGroup>
          <FormGroup label="BCD Rate %"><Input type="number" value={form.bcd_rate} onChange={e=>setForm(f=>({...f,bcd_rate:e.target.value}))} /></FormGroup>
          <FormGroup label="Drawback Rate %"><Input type="number" value={form.drawback_rate} onChange={e=>setForm(f=>({...f,drawback_rate:e.target.value}))} /></FormGroup>
          <FormGroup label="RoDTEP Rate %"><Input type="number" value={form.rodtep_rate} onChange={e=>setForm(f=>({...f,rodtep_rate:e.target.value}))} /></FormGroup>
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ORDER TRAIL
// ══════════════════════════════════════════════════════════
export function OrderTrail() {
  const [soData] = useData(() => soAPI.list());
  const [selectedSO, setSelectedSO] = useState('');
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadTrail = async (soId) => {
    if (!soId) return;
    setLoading(true);
    try {
      const r = await reportsAPI.orderTrail(soId);
      setTrail(r.data);
    } catch { toast.error('Failed to load trail'); }
    finally { setLoading(false); }
  };

  const sos = soData?.sales_orders || [];
  const steps = trail ? [
    { done:true, label:'Sales Order', detail:`${trail.so?.so_number} — ${trail.so?.party_name} — ${trail.so?.currency} ${trail.so?.total_amount}` },
    { done:trail.purchase_orders?.length>0, label:'Purchase Order', detail: trail.purchase_orders?.map(p=>`${p.po_number} — ${p.supplier_name} — ${fmtAmt(p.grand_total)}`).join(', ') || 'Not yet placed' },
    { done:trail.containers?.length>0, label:'Container Booked', detail: trail.containers?.map(c=>`${c.container_number} | ${c.vessel_name} | ${c.pol} → ${c.pod}`).join(', ') || 'Not booked' },
    { done:trail.shipping_bills?.length>0, label:'Shipping Bill Filed', detail: trail.shipping_bills?.map(s=>`SB ${s.sb_number} | Drawback ₹${s.drawback_amount}`).join(', ') || 'Not filed' },
    { done:trail.invoices?.length>0, label:'Sales Invoice', detail: trail.invoices?.map(i=>`${i.invoice_number} — ${i.currency} ${i.grand_total}`).join(', ') || 'Not created' },
  ] : [];

  return (
    <div>
      <div className="mb-5"><h1 className="text-xl font-black text-slate-800">🔗 Order Trail</h1><p className="text-sm text-slate-400">Complete lifecycle of any Sales Order</p></div>
      <Card className="mb-5">
        <div className="p-4 flex items-center gap-3">
          <FormGroup label="Select Sales Order" className="flex-1">
            <Select value={selectedSO} onChange={e=>{setSelectedSO(e.target.value);loadTrail(e.target.value);}}>
              <option value="">— Select a Sales Order —</option>
              {sos.map(s=><option key={s.id} value={s.id}>{s.so_number} — {s.party_name} ({s.currency} {s.total_amount})</option>)}
            </Select>
          </FormGroup>
        </div>
      </Card>
      {loading && <Spinner />}
      {trail && !loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader title="Lifecycle Trail" />
              <div className="p-4">
                {steps.map((step, i) => (
                  <div key={i} className="flex gap-3 pb-4 relative">
                    {i < steps.length-1 && <div className="absolute left-[9px] top-5 bottom-0 w-0.5 bg-slate-200"></div>}
                    <div className={`w-5 h-5 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center text-xs ${step.done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                      {step.done ? '✓' : i+1}
                    </div>
                    <div>
                      <div className={`font-bold text-sm ${step.done ? 'text-slate-800' : 'text-slate-400'}`}>{step.label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{step.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <div>
            <Card>
              <CardHeader title="SO Details" />
              <div className="p-4 space-y-2">
                {[['SO Number', trail.so?.so_number],['Buyer', trail.so?.party_name],['Value', `${trail.so?.currency} ${trail.so?.total_amount}`],['Incoterms', trail.so?.incoterms],['Status', trail.so?.status]].map(([l,v])=>(
                  <div key={l} className="flex justify-between text-sm border-b border-slate-100 pb-2"><span className="text-slate-400">{l}</span><span className="font-semibold text-slate-700">{v}</span></div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// GRN
// ══════════════════════════════════════════════════════════
export function GRN() {
  const [data, loading, reload] = useData(() => grnAPI.list());
  const [poData] = useData(() => poAPI.list({ status:'confirmed' }));
  const [supplierData] = useData(() => partiesAPI.list({ type:'supplier' }));
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ grn_date:new Date().toISOString().split('T')[0], po_id:'', party_id:'', vehicle_no:'', dc_number:'', received_by:'', qc_status:'accepted', qc_remarks:'' });
  const [saving, setSaving] = useState(false);
  const grns = data?.grn || [];
  const save = async () => {
    if (!form.po_id || !form.party_id) return toast.error('PO and supplier required');
    setSaving(true);
    try { await grnAPI.create(form); toast.success('GRN saved!'); setModal(false); reload(); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">✅ Goods Receipt (GRN)</h1></div>
        <div className="ml-auto"><Btn variant="success" onClick={()=>setModal(true)}>+ New GRN</Btn></div>
      </div>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['GRN No.','Date','Supplier','PO','Vehicle','DC No.','QC Status','Status']}>
            {grns.map(r=>(
              <Tr key={r.id}>
                <Td><span className="font-bold text-green-600 font-mono text-xs">{r.grn_number}</span></Td>
                <Td>{fmtDate(r.grn_date)}</Td>
                <Td>{r.supplier_name}</Td>
                <Td><Badge color="purple">{r.po_number}</Badge></Td>
                <Td mono>{r.vehicle_no||'—'}</Td>
                <Td>{r.dc_number||'—'}</Td>
                <Td><StatusBadge status={r.qc_status} /></Td>
                <Td><StatusBadge status={r.status} /></Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>
      <Modal open={modal} onClose={()=>setModal(false)} title="New GRN" size="md"
        footer={<><Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn><Btn variant="success" loading={saving} onClick={save}>✓ Save GRN</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Purchase Order" required>
            <Select value={form.po_id} onChange={e=>setForm(f=>({...f,po_id:e.target.value}))}>
              <option value="">— Select PO —</option>
              {(poData?.purchase_orders||[]).map(p=><option key={p.id} value={p.id}>{p.po_number} — {p.supplier_name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Supplier" required>
            <Select value={form.party_id} onChange={e=>setForm(f=>({...f,party_id:e.target.value}))}>
              <option value="">— Select —</option>
              {(supplierData?.parties||[]).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="GRN Date"><Input type="date" value={form.grn_date} onChange={e=>setForm(f=>({...f,grn_date:e.target.value}))} /></FormGroup>
          <FormGroup label="Vehicle No."><Input value={form.vehicle_no} onChange={e=>setForm(f=>({...f,vehicle_no:e.target.value}))} /></FormGroup>
          <FormGroup label="DC Number"><Input value={form.dc_number} onChange={e=>setForm(f=>({...f,dc_number:e.target.value}))} /></FormGroup>
          <FormGroup label="Received By"><Input value={form.received_by} onChange={e=>setForm(f=>({...f,received_by:e.target.value}))} /></FormGroup>
          <FormGroup label="QC Status">
            <Select value={form.qc_status} onChange={e=>setForm(f=>({...f,qc_status:e.target.value}))}>
              <option value="accepted">Accepted</option>
              <option value="accepted_with_remarks">Accepted with Remarks</option>
              <option value="rejected">Rejected</option>
            </Select>
          </FormGroup>
          <FormGroup label="QC Remarks"><Input value={form.qc_remarks} onChange={e=>setForm(f=>({...f,qc_remarks:e.target.value}))} /></FormGroup>
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// RECEIVABLES / PAYABLES
// ══════════════════════════════════════════════════════════
export function Receivables() {
  const [data, loading] = useData(() => reportsAPI.receivables());
  const rows = data?.receivables || [];
  const total = rows.reduce((s,r)=>s+parseFloat(r.grand_total||0),0);
  return (
    <div>
      <div className="mb-5"><h1 className="text-xl font-black text-slate-800">💚 Receivables</h1><p className="text-sm text-slate-400">Outstanding export payments from buyers</p></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <StatCard icon="💰" label="Total Outstanding" value={fmtAmt(total)} color="green" />
        <StatCard icon="⚠️" label="Overdue (>0 days)" value={rows.filter(r=>r.age_days>0).length} color="red" />
        <StatCard icon="🕐" label="Due This Month" value={rows.filter(r=>r.age_days<=30&&r.age_days>0).length} color="amber" />
      </div>
      <Card>
        {loading?<Spinner/>:(
          <Table headers={['Invoice No.','Buyer','Amount','Currency','Invoice Date','Due Date','Age (Days)','Status']}>
            {rows.map((r,i)=>(
              <Tr key={i}>
                <Td mono>{r.invoice_number}</Td>
                <Td><span className="font-semibold">{r.buyer_name}</span></Td>
                <Td right><span className="font-bold">{parseFloat(r.grand_total).toLocaleString()}</span></Td>
                <Td>{r.currency}</Td>
                <Td>{fmtDate(r.invoice_date)}</Td>
                <Td>{fmtDate(r.due_date)}</Td>
                <Td right><span className={r.age_days>60?'text-red-600 font-bold':r.age_days>30?'text-amber-600 font-bold':'text-slate-600'}>{r.age_days} days</span></Td>
                <Td><StatusBadge status={r.payment_status} /></Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

export function Payables() {
  const [data, loading] = useData(() => reportsAPI.payables());
  const rows = data?.payables || [];
  const total = rows.reduce((s,r)=>s+parseFloat(r.total_amount||0),0);
  return (
    <div>
      <div className="mb-5"><h1 className="text-xl font-black text-slate-800">🔴 Payables</h1><p className="text-sm text-slate-400">Outstanding payments to suppliers</p></div>
      <StatCard icon="💸" label="Total Payable" value={fmtAmt(total)} color="red" />
      <Card className="mt-4">
        {loading?<Spinner/>:(
          <Table headers={['Bill No.','Supplier','Amount (₹)','Bill Date','Due Date','Age (Days)','Status']}>
            {rows.map((r,i)=>(
              <Tr key={i}>
                <Td mono>{r.bill_number}</Td>
                <Td><span className="font-semibold">{r.supplier_name}</span></Td>
                <Td right><span className="font-bold">{fmtAmt(r.total_amount)}</span></Td>
                <Td>{fmtDate(r.bill_date)}</Td>
                <Td>{fmtDate(r.due_date)}</Td>
                <Td right><span className={r.age_days>0?'text-red-600 font-bold':'text-slate-600'}>{r.age_days} days</span></Td>
                <Td><StatusBadge status={r.payment_status} /></Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PLACEHOLDER PAGES
// ══════════════════════════════════════════════════════════
const PlaceholderPage = ({ title, icon }) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center"><div className="text-4xl mb-3">{icon}</div><h2 className="text-lg font-bold text-slate-600">{title}</h2><p className="text-sm text-slate-400 mt-1">Module coming soon</p></div>
  </div>
);
export const Alerts    = () => <PlaceholderPage title="Alerts & Tasks" icon="🔔" />;
export const ShippingBills = () => <PlaceholderPage title="Shipping Bills" icon="📋" />;
export const Documents = () => <PlaceholderPage title="Trade Documents" icon="📄" />;
export const Customs   = () => <PlaceholderPage title="Customs / CHA" icon="🛃" />;
export const GST       = () => <PlaceholderPage title="GST / ITC Management" icon="🧮" />;
export const DGFT      = () => <PlaceholderPage title="DGFT Licences" icon="📜" />;
export const MIS       = () => <PlaceholderPage title="MIS Reports" icon="📈" />;
export const Settings  = () => <PlaceholderPage title="Settings" icon="⚙️" />;
